import { Link, useLocation } from "react-router-dom";
import { Wallet, Home, Plus, Image, BarChart3, LogOut } from "lucide-react";
import { useState, useEffect } from "react";
import { useWallet } from "../contexts/WalletContext";
import Toast from "./Toast";

export default function Navbar() {
  const location = useLocation();
  const { account, isConnected, isOwner, chainId, connectWallet, disconnectWallet } = useWallet();
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [prevConnected, setPrevConnected] = useState(isConnected);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    if (isConnected && !prevConnected) setToast({ message: "Wallet connected successfully!", type: "success" });
    else if (!isConnected && prevConnected) setToast({ message: "Wallet disconnected", type: "success" });
    setPrevConnected(isConnected);
  }, [isConnected]);

  const handleConnect = async () => {
    try {
      setConnecting(true);
      await connectWallet();
    } catch (e: any) {
      setToast({ message: e.message || "Failed to connect wallet", type: "error" });
    } finally {
      setConnecting(false);
    }
  };

  const isActive = (path: string) => location.pathname === path;

  const navLinks = [
    { path: "/",          label: "Marketplace",   icon: Home },
    ...(isOwner ? [{ path: "/create", label: "Mint NFT", icon: Plus }] : []),
    { path: "/my-nfts",   label: "My NFTs",       icon: Image },
    { path: "/dashboard", label: "Dashboard",     icon: BarChart3 },
  ];

  const fmt = (a: string) => `${a.slice(0, 6)}...${a.slice(-4)}`;

  return (
    <>
      <nav className="bg-white shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Brand */}
            <Link to="/" className="flex items-center gap-2">
              <Wallet className="w-8 h-8 text-blue-600" />
              <span className="text-xl font-bold text-gray-800">NFT Marketplace</span>
            </Link>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                      isActive(link.path) ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {link.label}
                  </Link>
                );
              })}
            </div>

            {/* Wallet */}
            <div className="flex items-center gap-2">
              {isConnected ? (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-3 px-4 py-2.5 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg shadow-sm">
                    <div className="relative">
                      <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
                      <div className="absolute inset-0 w-2.5 h-2.5 bg-green-500 rounded-full animate-ping opacity-75" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs font-semibold text-green-700 uppercase tracking-wide">Connected</span>
                      <span className="text-sm font-mono font-bold text-gray-800">{fmt(account)}</span>
                    </div>
                  </div>
                  <button
                    onClick={disconnectWallet}
                    className="p-2.5 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-200"
                    title="Disconnect"
                  >
                    <LogOut className="w-5 h-5 text-red-600" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleConnect}
                  disabled={connecting}
                  className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg font-semibold transition-all shadow-md hover:shadow-lg transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Wallet className="w-4 h-4" />
                  <span className="hidden sm:inline">{connecting ? "Connecting..." : "Connect MetaMask"}</span>
                </button>
              )}
            </div>

            {/* Mobile nav icons */}
            <div className="md:hidden flex items-center gap-1">
              {navLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`p-2 rounded-lg transition-colors ${
                      isActive(link.path) ? "bg-blue-600 text-white" : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </nav>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} chainId={chainId} />}
    </>
  );
}
